import PropTypes from 'prop-types';
import { BsBullseye } from 'react-icons/bs';

import Switch from '#globalComponents/Forms/Switch';

import { FACET_LABELS, isOfferedFacet, vocabularyOptions } from '../../../utils/targetingLabels';

import { CollapseCard } from './CollapseCard';
import { CompanyKindSelect, summarizeCompanyKinds } from './CompanyKindSelect';
import { SegmentedToggle, TextInput } from './criteriaFormAtoms';
import { MultiSelect } from './MultiSelect';

const FULLTEXT_MODE_OPTIONS = [
	{ value: 'all', label: 'alle Begriffe' },
	{ value: 'any', label: 'einer der Begriffe' },
];

const VERTICAL_LABELS = {
	shk: 'SHK',
	elektro: 'Elektro',
	energie: 'Energie',
	bau: 'Bau',
	handwerk: 'Handwerk',
	dienstleistung: 'Dienstleistung',
	logistik: 'Logistik',
	kfz: 'Kfz',
	industrie: 'Industrie',
	lebensmittel: 'Lebensmittel',
	handel: 'Handel',
	it: 'IT',
	marketing: 'Marketing',
	beratung: 'Beratung',
	recht_steuern: 'Recht & Steuern',
	immobilien: 'Immobilien',
	finanzen: 'Finanzen',
	gesundheit: 'Gesundheit',
	gastronomie: 'Gastronomie',
	bildung: 'Bildung',
	landwirtschaft: 'Landwirtschaft',
	freizeit: 'Freizeit',
};

// Website-profile attribute → vocabulary list and placeholder of its select.
const FACET_SELECTS = [
	{ facet: 'customerTypes', list: 'customerTypes', placeholder: 'Kundentyp wählen' },
	{ facet: 'businessModel', list: 'businessModels', placeholder: 'Geschäftsmodell wählen' },
	{ facet: 'serviceArea', list: 'serviceAreas', placeholder: 'Servicegebiet wählen' },
	{ facet: 'tech', list: 'tech', placeholder: 'Technik wählen' },
];

const groupedOfferingOptions = (offerings = []) => {
	const sorted = [...offerings].sort(
		(a, b) => a.vertical.localeCompare(b.vertical) || a.label.localeCompare(b.label, 'de'),
	);
	return vocabularyOptions(sorted).map((option, index) => ({
		...option,
		group: VERTICAL_LABELS[sorted[index].vertical] ?? sorted[index].vertical,
	}));
};

const hiringSummary = (filters) => {
	const occupations = filters.hiringOccupations?.length ?? 0;
	return occupations > 0 ? `stellt ein (${occupations} Berufsfelder)` : 'stellt ein';
};

/**
 * One-line summary of the audience criteria for the collapsed card.
 * @param {Object} filters - UI filter state
 * @param {Object|undefined} vocabulary
 * @returns {string}
 */
const summarizeAudience = (filters, vocabulary) => {
	const bits = [];
	const kinds = summarizeCompanyKinds(filters, vocabulary);
	if (kinds) bits.push(kinds);
	if (filters.fulltextQuery?.trim()) bits.push('Volltext');
	const facetCount = ['customerTypes', 'businessModel', 'offerings', 'serviceArea', 'tech'].reduce(
		(sum, field) => sum + (filters[field]?.length ?? 0),
		0,
	);
	if (facetCount > 0) bits.push(`${facetCount} Merkmale`);
	if (filters.specializations?.trim()) bits.push('Spezialisierung');
	if (filters.hiringActive === true) bits.push(hiringSummary(filters));
	return bits.length ? bits.join(' · ') : 'Nicht gesetzt';
};

const FieldLabel = ({ children }) => <label className="block text-xs text-bgBase-800 mb-1">{children}</label>;

FieldLabel.propTypes = { children: PropTypes.node.isRequired };

const AudienceTargetingFields = ({ filters, onFilterChange, vocabulary }) => {
	const offered = (facet) => isOfferedFacet(vocabulary, facet);
	const facetSelects = FACET_SELECTS.filter(({ facet }) => offered(facet));

	return (
		<>
			<div className="space-y-2">
				<TextInput
					id="criteria-fulltext"
					label="Volltext auf der Website"
					placeholder="z.B. Fachanwalt für Erbrecht, Nachlass"
					value={filters.fulltextQuery}
					onChange={(v) => onFilterChange('fulltextQuery', v)}
				/>
				<div className="flex items-center justify-between gap-3">
					<SegmentedToggle
						options={FULLTEXT_MODE_OPTIONS}
						value={filters.fulltextMode || 'all'}
						onChange={(v) => onFilterChange('fulltextMode', v)}
					/>
					<span className="text-[10px] text-bgBase-800">Begriffe mit Komma trennen</span>
				</div>
			</div>
			<div className="grid grid-cols-2 gap-3">
				{offered('offerings') && (
					<div className="col-span-2">
						<FieldLabel>Leistungen</FieldLabel>
						<MultiSelect
							options={groupedOfferingOptions(vocabulary?.offerings)}
							selected={filters.offerings || []}
							onChange={(v) => onFilterChange('offerings', v)}
							placeholder="Leistungen wählen"
							searchable
						/>
					</div>
				)}
				{facetSelects.map(({ facet, list, placeholder }) => (
					<div key={facet}>
						<FieldLabel>{FACET_LABELS[facet]}</FieldLabel>
						<MultiSelect
							options={vocabularyOptions(vocabulary?.[list])}
							selected={filters[facet] || []}
							onChange={(v) => onFilterChange(facet, v)}
							placeholder={placeholder}
						/>
					</div>
				))}
				{offered('specializations') && (
					<TextInput
						id="criteria-specializations"
						label="Spezialisierung (kommagetrennt)"
						placeholder="z.B. Fachanwalt für Erbrecht, Meisterbetrieb"
						value={filters.specializations}
						onChange={(v) => onFilterChange('specializations', v)}
					/>
				)}
			</div>
			{offered('hiringActive') && (
				<Switch
					size="sm"
					checked={filters.hiringActive === true}
					onChange={(checked) => onFilterChange('hiringActive', checked ? true : null)}
					label="Stellt ein (Stellenanzeige in den letzten 90 Tagen oder Karriereseite)"
				/>
			)}
			{filters.hiringActive === true && offered('hiringOccupations') && (
				<div>
					<FieldLabel>Gesuchte Berufsfelder</FieldLabel>
					<MultiSelect
						options={vocabularyOptions(vocabulary?.occupations)}
						selected={filters.hiringOccupations || []}
						onChange={(v) => onFilterChange('hiringOccupations', v)}
						placeholder="Alle Berufsfelder"
						searchable
					/>
				</div>
			)}
			<Switch
				size="sm"
				checked={filters.looseFacets === true}
				onChange={(checked) => onFilterChange('looseFacets', checked)}
				label="Auch unsichere Zuordnungen (Hinweise) einbeziehen"
			/>
		</>
	);
};

AudienceTargetingFields.propTypes = {
	filters: PropTypes.object.isRequired,
	onFilterChange: PropTypes.func.isRequired,
	vocabulary: PropTypes.object,
};

/**
 * "Zielgruppe" card: the company kinds a search keeps (register-derived, always offered), and with
 * targeting search the website full text and the website-profile attributes. Only attributes the
 * tagger evaluation cleared as hard filters are offered.
 */
export const AudienceCriteriaCard = ({ filters, onFilterChange, vocabulary, targetingEnabled, open, onToggle }) => (
	<CollapseCard
		icon={BsBullseye}
		title="Zielgruppe"
		type="hr"
		span2
		summary={summarizeAudience(filters, vocabulary)}
		open={open}
		onToggle={onToggle}
	>
		<CompanyKindSelect filters={filters} onFilterChange={onFilterChange} vocabulary={vocabulary} />
		{targetingEnabled && (
			<AudienceTargetingFields filters={filters} onFilterChange={onFilterChange} vocabulary={vocabulary} />
		)}
	</CollapseCard>
);

AudienceCriteriaCard.propTypes = {
	filters: PropTypes.object.isRequired,
	onFilterChange: PropTypes.func.isRequired,
	vocabulary: PropTypes.object,
	targetingEnabled: PropTypes.bool.isRequired,
	open: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
};
